import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import useInfiniteQuery from 'hooks/useInfiniteQuery';
import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { reviewQueryKeys } from 'lib/queryKeys';

import { getAliasReviewsList } from 'services/alias';

import ReviewCard from './ReviewCard';
import ReviewsSummary from './ReviewsSummary';

import COMMENT from 'constants/comment';
import OFFER from 'constants/offer';

const REVIEWS_LIMIT = 15;

const normalizeReview = (review) => ({
  ...review,
  stars: review.stars || OFFER.REVIEWS.DEFAULT_RATING_VALUE,
});

/**
 * Envelopa `initialReviews` no shape esperado por `useInfiniteQuery` do
 * tanstack v4: `{ pages, pageParams }`. Passar array direto faz
 * `state.data.pages` ficar `undefined`, o que trava `hasNextPage` em
 * `undefined` e impede a paginação infinita.
 */
const buildInitialData = (initialReviews) => {
  if (!Array.isArray(initialReviews) || initialReviews.length === 0) {
    return undefined;
  }

  return {
    pages: [initialReviews],
    pageParams: [undefined],
  };
};

const ReviewsList = ({
  aliasId,
  averageStars = 0,
  className,
  initialReviews = [],
  onLoadedCountChange,
  totalReviews = 0,
}) => {
  const fetchNextPageRef = useRef(null);

  const {
    data: reviews,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: reviewQueryKeys.list({
      aliasId,
      sort: COMMENT.REVIEWS_SORT,
    }),
    // Sem `aliasId` o endpoint `/v4/aliases/{id}/reviews` não pode ser
    // chamado, então desabilitamos o fetch e ficamos apenas com o que vier
    // do SSR (se houver). A aba de Avaliações continua acessível: o usuário
    // pode enviar uma avaliação — ela fica pendente no backend até um alias
    // ser atribuído à oferta.
    enabled: Boolean(aliasId),
    queryFn: async (after, { signal }) => {
      const response = await getAliasReviewsList(
        {
          after,
          aliasId,
          limit: REVIEWS_LIMIT,
        },
        { signal }
      );
      return response?.data ?? [];
    },
    initialData: buildInitialData(initialReviews),
    // `lastPage` é o array de reviews da última página, `allPages` é o array
    // de todas as páginas já carregadas. Usamos `totalReviews` (contagem real
    // do alias) em vez de comparar `lastPage.length >= LIMIT` — isso funciona
    // tanto na transição SSR (limit=3) quanto em páginas seguintes (limit=15).
    getNextPageParam: (lastPage, allPages) => {
      const loaded = (allPages || []).flat().length;
      if (loaded >= totalReviews) return undefined;
      return lastPage?.at(-1)?.id;
    },
    select: (pages) => (pages || []).map(normalizeReview),
    refetchOnMount: false,
    staleTime: Infinity,
  });

  // `threshold: 0` + `rootMargin` fazem o observer disparar assim que o
  // sentinel se aproxima do viewport (em vez de exigir 100% visible — o que
  // nunca acontece para uma div de altura zero dentro de um container com
  // overflow-y-auto). Sem isso, o fetchNextPage só dispara após múltiplos
  // scrolls forçados ao fim da lista.
  useIntersectionObserver({
    enabled: hasNextPage,
    rootMargin: '800px',
    target: fetchNextPageRef,
    threshold: 0,
    onIntersect: fetchNextPage,
  });

  // Eager load: quando o ReviewsList monta (usuário ativa a aba Avaliações)
  // e o SSR entregou só uma parte do total (limit=3), dispara uma busca
  // imediatamente sem esperar pelo scroll. Evita que o usuário veja a
  // scrollbar no fim sem saber que faltam reviews. Só dispara uma vez;
  // páginas seguintes continuam pelo observer.
  const hasTriggeredEagerFetchRef = useRef(false);
  useEffect(() => {
    if (
      hasNextPage &&
      !isFetchingNextPage &&
      !hasTriggeredEagerFetchRef.current
    ) {
      hasTriggeredEagerFetchRef.current = true;
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  /*
    Propaga a contagem real de reviews carregadas pra o caller — o
    badge da aba "Avaliações" usa isso pra mostrar o número correto
    quando `offer.totalReviews` vem 0/undefined da API mesmo havendo
    reviews no array. Reage tanto a paginação (count cresce) quanto
    a envio de novo review (mutation otimista).
  */
  useEffect(() => {
    if (onLoadedCountChange) {
      onLoadedCountChange(reviews.length);
    }
  }, [reviews.length, onLoadedCountChange]);

  /*
    Separa os dois cenários distintos de "sem avaliações":
    - Sem alias: o produto ainda não tem ficha de alias — o review será
      criado pendente no backend e aprovado quando o alias existir. Não
      faz sentido dizer "seja a primeira pessoa a avaliar" porque o
      usuário não verá o review aparecer imediatamente.
    - Com alias mas sem reviews: lista genuinamente vazia — aí sim o
      incentivo faz sentido.
  */
  if (reviews.length === 0) {
    return (
      <div
        className={twMerge(
          'flex flex-col items-center justify-center gap-y-2 py-8 text-center text-neutral-low-300',
          className
        )}
        data-testid="reviews-list-empty"
      >
        <p className="text-sm">Ainda não há avaliações para este produto.</p>
        {aliasId && (
          <p className="text-xs">Seja a primeira pessoa a avaliar.</p>
        )}
      </div>
    );
  }

  return (
    <div
      className={twMerge('flex flex-col gap-y-4', className)}
      data-testid="reviews-list"
    >
      <ReviewsSummary averageStars={averageStars} />
      <ul className="flex list-none flex-col gap-y-3 p-0">
        {reviews.map((review) => (
          <li key={review.id}>
            <ReviewCard
              aliasId={aliasId}
              hasImage={Boolean(review.hasImage)}
              review={review}
            />
          </li>
        ))}
      </ul>
      {isFetchingNextPage && (
        <div
          className="rounded h-4 w-full animate-pulse bg-neutral-high-300"
          data-testid="reviews-list-loading"
        />
      )}
      <div ref={fetchNextPageRef} />
    </div>
  );
};

ReviewsList.propTypes = {
  /**
   * Alias id used to fetch the reviews list for this product
   */
  aliasId: PropTypes.number,
  /**
   * Average stars rating for the alias. Rendered at the top of the list.
   */
  averageStars: PropTypes.number,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /**
   * Reviews rendered during SSR. Usado como página inicial do infinite query
   * para evitar refetch na hidratação.
   */
  initialReviews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      date: PropTypes.string.isRequired,
      likes: PropTypes.number,
      stars: PropTypes.number,
      text: PropTypes.string.isRequired,
      user: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        photo: PropTypes.string,
      }),
    })
  ),
  /**
   * Callback chamado sempre que a quantidade de reviews carregadas muda
   * (paginação, hidratação SSR, mutation pós-envio). Usado pelo caller
   * pra refletir o número real no badge da aba quando o backend não
   * retorna `totalReviews` corretamente.
   */
  onLoadedCountChange: PropTypes.func,
  /**
   * Total number of reviews associated with the alias. Usado para decidir se
   * há mais páginas a buscar.
   */
  totalReviews: PropTypes.number,
};

export default ReviewsList;
